// Notification API Service

import api from './api';

export type NotificationWorthiness = 'yes' | 'maybe' | 'no';
export type ActionUsefulness = 'yes' | 'partial' | 'no';

export type NotificationSeverity = 'normal' | 'critical';

export interface NotificationItem {
  id: string;
  notification_type: string;
  severity: NotificationSeverity;
  title: string;
  message: string;
  link: string | null;
  is_read: boolean;
  is_dismissed: boolean;
  dismissed_at: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  // Save-for-later on a critical alert (owner ruling, review fmbdf6): the
  // alert stays on the bar, muted, optionally until a come-back date.
  saved_for_later_at?: string | null;
  saved_for_later_until?: string | null;
  saved_for_later_by?: string | null;
  saved_for_later_note?: string | null;
  saved_for_later_returned_at?: string | null;
  recipient_salesperson_no: string | null;
  extra_data: Record<string, unknown> | null;
  // AI recommended action
  recommended_action: string | null;
  recommended_action_reasoning: string | null;
  // Simple feedback (legacy)
  user_feedback: 'thumbs_up' | 'thumbs_down' | null;
  feedback_text: string | null;
  feedback_at: string | null;
  // Structured feedback dimensions
  feedback_notification_worth: NotificationWorthiness | null;
  feedback_category_accurate: boolean | null;
  feedback_category_override: string | null;
  feedback_action_useful: ActionUsefulness | null;
  agent_decision: 'notify' | 'unsure' | 'ignore' | null;
  read_at: string | null;
  created_at: string;
}

export interface FeedbackResponse {
  notification_id: string;
  feedback: string | null;
  knowledge_updated: boolean;
  notification_worth: NotificationWorthiness | null;
  category_accurate: boolean | null;
  category_override: string | null;
  action_useful: ActionUsefulness | null;
}

export interface StructuredFeedbackRequest {
  // Simple feedback (legacy)
  feedback?: 'thumbs_up' | 'thumbs_down';
  feedback_text?: string;
  // Structured feedback dimensions
  notification_worth?: NotificationWorthiness;
  category_accurate?: boolean;
  category_override?: string;
  action_useful?: ActionUsefulness;
}

export interface NotificationListResponse {
  notifications: NotificationItem[];
  total_unread: number;
  limit: number;
  offset: number;
}

export interface UnreadCountResponse {
  unread_count: number;
}

export interface ActionItemsResponse {
  items: NotificationItem[];
  total: number;
}

export interface CriticalAlertsResponse {
  alerts: NotificationItem[];
  total: number;
  /**
   * Ids of alerts that must be cleared with an outcome rather than a plain
   * acknowledge. Served by the backend so the scoping rule lives in one place.
   */
  outcome_required_ids?: string[];
  /** Per-alert bar state served by the backend: active | saved_for_later | returned. */
  alert_states?: Record<string, CriticalAlertState>;
  /** Alerts whose come-back date passed: id -> the note the bar shows. */
  returned_notes?: Record<string, string>;
}

export type CriticalAlertState = 'active' | 'saved_for_later' | 'returned';

/** How a CSR resolved a must-acknowledge audit alert. */
export type AlertOutcome = 'fixed' | 'no_change_needed';

/** Live-Sage verdict used to steer the CSR toward the honest outcome. */
export type ResolutionVerdict = 'resolved' | 'unresolved' | 'unknown';

export interface ResolutionCheckResponse {
  verdict: ResolutionVerdict;
  reason: string;
  sage_order_no: string | null;
  divergences: string[];
  /** Null when we genuinely cannot tell — show both options neutrally. */
  suggested_outcome: AlertOutcome | null;
}

export interface AlertOutcomeResponse {
  notification_id: string;
  outcome: AlertOutcome | null;
  already_recorded: boolean;
  /**
   * True when the alert is acknowledged (off the critical bar) after this
   * call — including an already-recorded outcome that had never been
   * acknowledged (written out of band by a follow-up audit pass).
   */
  cleared: boolean;
  sage_verdict: ResolutionVerdict | null;
  /** Non-empty when the CSR's answer disagrees with live Sage. Advisory. */
  contradiction: string;
  feedback_queued: boolean;
  divergences: string[];
}

export interface ActionItemRule {
  label: string;
  types: string[];
  description: string;
}

export interface ActionItemRulesResponse {
  rules: ActionItemRule[];
  eligible_types: string[];
}

export const notificationService = {
  /**
   * List notifications
   */
  listNotifications: async (params?: {
    unread_only?: boolean;
    include_dismissed?: boolean;
    notification_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<NotificationListResponse> => {
    const response = await api.get<NotificationListResponse>(
      '/notifications',
      { params },
    );
    return response.data;
  },

  /**
   * Fresh unread bell-only count — drives the bell's dot indicator.
   * Action-item types are excluded (they get the numeric badge).
   */
  getBellIndicator: async (): Promise<number> => {
    const response = await api.get<{ fresh_unread_count: number }>(
      '/notifications/bell-indicator',
    );
    return response.data.fresh_unread_count;
  },

  /**
   * Get unread count for badge
   */
  getUnreadCount: async (): Promise<number> => {
    const response = await api.get<UnreadCountResponse>(
      '/notifications/unread-count',
    );
    return response.data.unread_count;
  },

  /**
   * Mark a single notification as read
   */
  markRead: async (notificationId: string): Promise<NotificationItem> => {
    const response = await api.patch<NotificationItem>(
      `/notifications/${notificationId}/read`,
    );
    return response.data;
  },

  /**
   * Mark all notifications as read
   */
  markAllRead: async (): Promise<{ marked_count: number }> => {
    const response = await api.post<{ marked_count: number }>(
      '/notifications/mark-all-read',
    );
    return response.data;
  },

  /**
   * Get action items for the sidebar (undismissed, actionable notifications)
   */
  getActionItems: async (limit: number = 20): Promise<ActionItemsResponse> => {
    const response = await api.get<ActionItemsResponse>(
      '/notifications/action-items',
      { params: { limit } },
    );
    return response.data;
  },

  /**
   * Get unacknowledged CRITICAL alerts for the pop-up notification bar.
   * These persist until explicitly acknowledged (read/dismiss do not clear).
   */
  getCriticalAlerts: async (limit: number = 20): Promise<CriticalAlertsResponse> => {
    const response = await api.get<CriticalAlertsResponse>(
      '/notifications/critical',
      { params: { limit } },
    );
    // Normalise at the boundary so every consumer can trust `alerts` to be an
    // array even if the api answers a partial body (kanban 90f3b9d8).
    const data = response.data ?? ({} as CriticalAlertsResponse);
    const alerts = Array.isArray(data.alerts) ? data.alerts : [];
    return { ...data, alerts, total: typeof data.total === 'number' ? data.total : alerts.length };
  },

  /**
   * Acknowledge a critical notification — the only action that clears it
   * from the pop-up bar.
   */
  /** Keep a critical alert on the bar, muted, optionally until an ISO date. */
  saveForLater: async (
    notificationId: string,
    until: string | null,
    note: string | null,
  ): Promise<NotificationItem> => {
    const response = await api.post<NotificationItem>(
      `/notifications/${notificationId}/save-for-later`,
      { until, note },
    );
    return response.data;
  },

  /** Undo save-for-later: the alert is red again right away. */
  bringBack: async (notificationId: string, reason?: string): Promise<NotificationItem> => {
    const response = await api.post<NotificationItem>(
      `/notifications/${notificationId}/bring-back`,
      { reason: reason ?? null },
    );
    return response.data;
  },

  acknowledge: async (notificationId: string): Promise<NotificationItem> => {
    const response = await api.post<NotificationItem>(
      `/notifications/${notificationId}/acknowledge`,
    );
    return response.data;
  },

  /**
   * Re-derive an alerted order against live Sage.
   *
   * The portal audit tables only record edits made through the portal, so a
   * CSR who fixed the order directly in Sage leaves no trace there. This is
   * the only way to tell "already corrected" from "never touched".
   */
  getResolutionCheck: async (
    notificationId: string,
  ): Promise<ResolutionCheckResponse> => {
    const response = await api.get<ResolutionCheckResponse>(
      `/notifications/${notificationId}/resolution-check`,
    );
    return response.data;
  },

  /**
   * Clear a must-acknowledge audit alert with an outcome.
   *
   * `no_change_needed` requires a substantive reason — it is the only record
   * of what the alert did not know.
   */
  submitAlertOutcome: async (
    notificationId: string,
    outcome: AlertOutcome,
    reason?: string,
  ): Promise<AlertOutcomeResponse> => {
    const response = await api.post<AlertOutcomeResponse>(
      `/notifications/${notificationId}/outcome`,
      { outcome, reason: reason ?? null },
    );
    return response.data;
  },

  /**
   * Get action item display rules (admin documentation)
   */
  getActionItemRules: async (): Promise<ActionItemRulesResponse> => {
    const response = await api.get<ActionItemRulesResponse>(
      '/notifications/action-items/rules',
    );
    return response.data;
  },

  /**
   * Dismiss a notification (mark action item as completed)
   */
  dismiss: async (notificationId: string): Promise<NotificationItem> => {
    const response = await api.patch<NotificationItem>(
      `/notifications/${notificationId}/dismiss`,
    );
    return response.data;
  },

  /**
   * Bulk-dismiss every action-item-eligible notification for the current user.
   * Clears the action-items banner in one shot.
   */
  dismissAllActionItems: async (): Promise<{ dismissed_count: number }> => {
    const response = await api.post<{ dismissed_count: number }>(
      '/notifications/dismiss-all-action-items',
    );
    return response.data;
  },

  /**
   * Undismiss a notification (undo action item completion)
   */
  undismiss: async (notificationId: string): Promise<NotificationItem> => {
    const response = await api.patch<NotificationItem>(
      `/notifications/${notificationId}/undismiss`,
    );
    return response.data;
  },

  /**
   * Submit feedback (thumbs up/down) on a notification
   */
  submitFeedback: async (
    notificationId: string,
    feedback: 'thumbs_up' | 'thumbs_down',
    feedbackText?: string,
  ): Promise<FeedbackResponse> => {
    const response = await api.post<FeedbackResponse>(
      `/notifications/${notificationId}/feedback`,
      {
        feedback,
        feedback_text: feedbackText || null,
      },
    );
    return response.data;
  },

  /**
   * Submit structured feedback on a notification (enhanced feedback)
   */
  submitStructuredFeedback: async (
    notificationId: string,
    feedbackData: StructuredFeedbackRequest,
  ): Promise<FeedbackResponse> => {
    const response = await api.post<FeedbackResponse>(
      `/notifications/${notificationId}/feedback`,
      feedbackData,
    );
    return response.data;
  },
};

export default notificationService;
